import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const REQUESTS_KEY = ['admin', 'requests'];

// GET /admin/requests — every request, oldest first
// (backend/src/repositories/requestRepository.js#list, no status/assignedTo
// filter applied here). Screen 1's own status filter (AllRequestsPage) is
// client-side over this one fetched list rather than a second query per
// filter choice — the dataset is one shop's request volume, not something
// that needs server-side pagination yet, and keeping ONE query key here
// keeps the assign mutation's cache update below a single, simple write
// instead of having to reconcile N filtered query keys.
export function useAdminRequests() {
  return useQuery({
    queryKey: REQUESTS_KEY,
    queryFn: () => apiClient.get('/admin/requests'),
    select: (data) => data.requests,
  });
}

// GET /admin/requests/:id — the full read-only request detail
// (backend/src/services/requestService.js#getRequestDetail): request +
// lines with each line's matched catalog product, the organization contact
// block, assignee/submitter/assigner names, every quotation revision, an
// activity timeline, and the notification-delivery log. One rich response,
// matching this codebase's pattern for GET /admin/quotations/:id.
export function useAdminRequestDetail(requestId) {
  return useQuery({
    queryKey: ['admin', 'requests', requestId, 'detail'],
    queryFn: () => apiClient.get(`/admin/requests/${requestId}`),
    enabled: Boolean(requestId),
    select: (data) => data.request,
  });
}

// GET /admin/requests/:id/uploads — the buyer's attached files, safe subset
// only (never storage_key). Same payload the sales workspace's attachment
// panel consumes, so the same AttachmentPanel component renders it.
export function useAdminRequestUploads(requestId) {
  return useQuery({
    queryKey: ['admin', 'requests', requestId, 'uploads'],
    queryFn: () => apiClient.get(`/admin/requests/${requestId}/uploads`),
    enabled: Boolean(requestId),
    select: (data) => data.uploads,
  });
}

// GET /admin/requests/:id/notes — the shared buyer/staff notes log, READ
// ONLY on this screen (the detail view adds no actions — assignment happens
// on the list). An admin may read any request's notes.
export function useAdminRequestNotes(requestId) {
  return useQuery({
    queryKey: ['admin', 'requests', requestId, 'notes'],
    queryFn: () => apiClient.get(`/admin/requests/${requestId}/notes`),
    enabled: Boolean(requestId),
    select: (data) => data.notes,
  });
}

// GET /admin/users — sales-role accounts (active and inactive), the
// assignee picker's data source. Inactive accounts are filtered out by the
// PAGE, not here — this hook returns the raw list so a future screen (e.g.
// a "users" admin screen) can still see inactive accounts if it needs to.
export function useAdminSalesUsers() {
  return useQuery({
    queryKey: ['admin', 'salesUsers'],
    queryFn: () => apiClient.get('/admin/users'),
    select: (data) => data.users,
  });
}

// POST /admin/requests/:id/assign — first assignment AND reassignment, same
// endpoint (backend/src/services/requestService.js#assignRequest). The
// response's `request` comes from requestRepository.findByIdWithLines,
// which has no assigned_to_name join (unlike list()'s own join) — merging
// it into the cache first gives an instant assigned_to/status update, then
// invalidating refetches the real, joined list so assigned_to_name catches
// up. Same two-step pattern as
// hooks/useApprovalDetail.js#useClearFloorLine, for the same reason.
export function useAssignRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, assignedTo }) =>
      apiClient.post(`/admin/requests/${requestId}/assign`, { assignedTo }),
    onSuccess: (data) => {
      queryClient.setQueryData(REQUESTS_KEY, (prev) => {
        if (!prev || !prev.requests) return prev;
        return {
          ...prev,
          requests: prev.requests.map((request) =>
            request.id === data.request.id ? { ...request, ...data.request } : request
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
    },
  });
}

// GET /admin/requests/metrics/turnaround — the average time from request
// received to proforma sent, over the last 90 days
// (backend/src/services/requestService.js#getProformaTurnaroundMetric,
// backend by requestRepository -> quotationRepository.getProformaTurnaroundMetric).
// `avgHours` is `null` when `sampleSize` is 0 — a brand-new shop with no
// proforma sent yet in the window, a normal first-time state (root
// CLAUDE.md's "define that state explicitly, never fall back to zero"
// principle), never rendered as an error or as "0h". Its own query key,
// separate from REQUESTS_KEY, since it's a wholly different read with no
// cache-write overlap with any request-list mutation here.
export function useProformaTurnaroundMetric() {
  return useQuery({
    queryKey: ['admin', 'turnaroundMetric'],
    queryFn: () => apiClient.get('/admin/requests/metrics/turnaround'),
  });
}

export default useAdminRequests;
